/**
 * Route chunk prefetch.
 *
 * Lazy-loaded routes (via React.lazy) only fetch their JS chunk when the
 * user navigates *to* them. That one-off network round-trip is the main
 * reason the first click on Library / Templates / Studio feels like a
 * 2–3 second hang.
 *
 * The fix: declare each route's `import()` once here, wrap it with a
 * memoised single-flight, and hand components the same promise factory.
 * Links call `prefetch(route)` on hover — by the time the user actually
 * clicks, the chunk is already in the browser cache.
 *
 * Every loader is a module-level `() => import()`, so Vite still generates
 * the same split chunk. Calling it twice returns the memoised promise.
 */

import { ROUTES, type AppRoute } from "@/types";

type Loader = () => Promise<unknown>;

const _cache = new Map<AppRoute, Promise<unknown>>();

function memoed(loader: Loader): Loader {
  return () => {
    const key = (loader as { __route?: AppRoute }).__route;
    if (key && _cache.has(key)) return _cache.get(key)!;
    const p = loader();
    if (key) _cache.set(key, p);
    return p;
  };
}

function mark<T extends Loader>(fn: T, route: AppRoute): T {
  (fn as unknown as { __route: AppRoute }).__route = route;
  return fn;
}

/** Per-route loader. Re-used by both React.lazy AND prefetch calls. */
export const routeLoaders: Record<AppRoute, Loader> = {
  [ROUTES.LOGIN]:       memoed(mark(() => import("@/pages/auth/LoginPage"),               ROUTES.LOGIN)),
  [ROUTES.SIGNUP]:      memoed(mark(() => import("@/pages/auth/SignupPage"),              ROUTES.SIGNUP)),
  [ROUTES.DASHBOARD]:   memoed(mark(() => import("@/pages/dashboard/DashboardPage"),      ROUTES.DASHBOARD)),
  // Studio route is now a redirect — point its loader at Dashboard so any
  // stale `prefetchRoute(STUDIO)` calls just warm the dashboard chunk.
  [ROUTES.STUDIO]:      memoed(mark(() => import("@/pages/dashboard/DashboardPage"),      ROUTES.STUDIO)),
  [ROUTES.LIBRARY]:     memoed(mark(() => import("@/pages/library/LibraryPage"),          ROUTES.LIBRARY)),
  [ROUTES.EDITOR]:      memoed(mark(() => import("@/pages/editor/EditorPage"),            ROUTES.EDITOR)),
  [ROUTES.CALENDAR]:    memoed(mark(() => import("@/pages/calendar/CalendarPage"),        ROUTES.CALENDAR)),
  [ROUTES.BRAND_KIT]:   memoed(mark(() => import("@/pages/brand-kit/BrandKitPage"),       ROUTES.BRAND_KIT)),
  [ROUTES.TEMPLATES]:   memoed(mark(() => import("@/pages/templates/TemplatesPage"),      ROUTES.TEMPLATES)),
  [ROUTES.INSPIRATION]: memoed(mark(() => import("@/pages/inspiration/InspirationPage"),  ROUTES.INSPIRATION)),
  [ROUTES.WORKSPACES]:  memoed(mark(() => import("@/pages/workspaces/WorkspacesPage"),    ROUTES.WORKSPACES)),
  [ROUTES.SETTINGS]:    memoed(mark(() => import("@/pages/settings/SettingsPage"),        ROUTES.SETTINGS)),
  [ROUTES.TEAM]:        memoed(mark(() => import("@/pages/team/TeamPage"),                ROUTES.TEAM)),
  [ROUTES.PLANS]:       memoed(mark(() => import("@/pages/plans/PlansPage"),              ROUTES.PLANS)),
  [ROUTES.ADMIN]:       memoed(mark(() => import("@/pages/admin/AdminPage"),              ROUTES.ADMIN)),
  [ROUTES.ONBOARDING_WORKSPACE]: memoed(mark(() => import("@/pages/onboarding/OnboardingWorkspacePage"), ROUTES.ONBOARDING_WORKSPACE)),
  [ROUTES.PRICING]:     memoed(mark(() => import("@/pages/pricing/PricingPage"),          ROUTES.PRICING)),
  [ROUTES.TERMS]:       memoed(mark(() => import("@/pages/legal/TermsOfService"),         ROUTES.TERMS)),
  [ROUTES.PRIVACY]:     memoed(mark(() => import("@/pages/legal/PrivacyPolicy"),          ROUTES.PRIVACY)),
  [ROUTES.COOKIES]:     memoed(mark(() => import("@/pages/legal/CookiePolicy"),           ROUTES.COOKIES)),
  [ROUTES.REFUND]:      memoed(mark(() => import("@/pages/legal/RefundPolicy"),           ROUTES.REFUND)),
};

type LazyModule = { default: React.ComponentType<unknown> };

/** Typed wrapper for React.lazy that also kicks the memo cache. */
export function lazyRoute(route: AppRoute) {
  return routeLoaders[route] as () => Promise<LazyModule>;
}

/** Trigger a chunk fetch without navigating. Safe to call many times. */
export function prefetchRoute(route: AppRoute) {
  const loader = routeLoaders[route];
  if (loader) loader().catch(() => { /* network/module errors handled on real nav */ });
}

/**
 * Warm the cache for the routes a signed-in user almost always lands on.
 * Run inside `requestIdleCallback` from AppLayout so we don't contend with
 * the initial render critical path.
 */
export function prefetchCommonRoutes() {
  prefetchRoute(ROUTES.LIBRARY);
  // Phase 0: Studio route redirects to Dashboard. The original prefetch
  // is no longer useful (its loader now points at Dashboard too).
  prefetchRoute(ROUTES.TEMPLATES);
  prefetchRoute(ROUTES.BRAND_KIT);
  prefetchRoute(ROUTES.CALENDAR);
  prefetchRoute(ROUTES.TEAM);
  prefetchRoute(ROUTES.PLANS);
}
